import React from "react";
import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import Checkbox from "./checkbox";

const styles = StyleSheet.create({
    section: {
        margin: 10,
        padding: 20,
        paddingTop: 0,
        paddingBottom: 0,
    },
    sectionTitle: {
        fontWeight: 800,
        fontSize: 13,
        marginBottom: 12,
        marginTop: 12,
    },
    pText: {
        fontWeight: 400,
        marginBottom: 12,
        fontSize: 11,
    },
    mutedText: {
        fontWeight: 400,
        marginBottom: 12,
        fontSize: 10,
        color: "#555555",
    },
    fontBold: {
        fontWeight: "bold",
    },
    infoRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 8,
    },
    infoLabel: {
        fontSize: 10,
        fontWeight: 600,
        color: "#1a73e8",
        width: "40%",
    },
    infoValue: {
        fontSize: 10,
        fontWeight: 400,
        width: "58%",
        textAlign: "right",
    },
    table: {
        display: "table",
        width: "auto",
        marginBottom: "10px",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#000",
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "1px solid #000",
    },
    tableLastRow: {
        borderBottom: "none",
    },
    tableColHeader: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#eeeeee",
        borderRight: "1px solid #000",
    },
    tableCol: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        borderRight: "1px solid #000",
        paddingTop: 2,
        paddingBottom: 2,
    },
    tableCell: {
        margin: 5,
        fontSize: 8,
        maxWidth: "100%",
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflow: "hidden",
    },
    gapReason: {
        margin: 5,
        marginTop: 0,
        fontSize: 7,
        color: "#555555",
    },
    borderRightNone: {
        borderRight: "none",
    },
    colObligation: {
        width: "70%",
    },
    colStatus: {
        width: "30%",
    },
    colTitle: {
        width: "40%",
    },
    colCategory: {
        width: "30%",
    },
    colType: {
        width: "30%",
    },
    colQuestion: {
        width: "55%",
    },
    colAnswer: {
        width: "45%",
    },
});

const KeyValueRows = ({ items, t }) => {
    if (!items?.length) {
        return (
            <Text style={styles.mutedText}>
                {t("No data available.")}
            </Text>
        );
    }

    return items.map((item) => (
        <View style={styles.infoRow} key={item.key || item.label}>
            <Text style={styles.infoLabel}>{t(item.label)}</Text>
            <Text style={styles.infoValue}>
                {item.value !== null && item.value !== undefined
                    ? t(String(item.value))
                    : "-"}
            </Text>
        </View>
    ));
};

const ReadinessSection = ({ readiness = {}, t }) => {
    const gapsCount =
        readiness.gapsCount === 0 || readiness.gapsCount === "0"
            ? "0"
            : readiness.gapsCount || "-";

    return (
        <View>
            <Text style={styles.sectionTitle}>
                {`1. ${t("CE Readiness & Decision")}`}
            </Text>

            <KeyValueRows
                t={t}
                items={[
                    {
                        key: "status",
                        label: t("CE Status"),
                        value: readiness.statusLabel
                            ? t(readiness.statusLabel)
                            : "-",
                    },
                    {
                        key: "gaps",
                        label: t("Open Gaps"),
                        value: gapsCount,
                    },
                ]}
            />

            {readiness.hasDecision ? (
                <>
                    <Text style={{ ...styles.pText, ...styles.fontBold, marginTop: 8 }}>
                        {t("Decision")}
                    </Text>
                    {readiness.decisionRows?.length > 0 ? (
                        <KeyValueRows t={t} items={readiness.decisionRows} />
                    ) : (
                        <Text style={styles.pText}>
                            {readiness.decisionRaw || "-"}
                        </Text>
                    )}
                </>
            ) : (
                <Text style={styles.mutedText}>
                    {t("No decision export available.")}
                </Text>
            )}
        </View>
    );
};

const ObligationsSection = ({ obligations = [], t }) => {
    return (
        <View>
            <Text style={styles.sectionTitle}>
                {`2. ${t("Obligation Checklist & Gaps")}`}
            </Text>

            {obligations.length === 0 ? (
                <Text style={styles.mutedText}>
                    {t("No obligations available.")}
                </Text>
            ) : (
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <View
                            style={{
                                ...styles.tableColHeader,
                                ...styles.colObligation,
                            }}
                        >
                            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
                                {t("Obligation")}
                            </Text>
                        </View>
                        <View
                            style={{
                                ...styles.tableColHeader,
                                ...styles.colStatus,
                                ...styles.borderRightNone,
                            }}
                        >
                            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
                                {t("Status")}
                            </Text>
                        </View>
                    </View>

                    {obligations.map((obligation, index) => {
                        const rowStyle = {
                            ...styles.tableRow,
                            ...((index + 1 >= obligations.length) &&
                                styles.tableLastRow),
                        };

                        return (
                            <View style={rowStyle} key={obligation.code || index}>
                                <View
                                    style={{
                                        ...styles.tableCol,
                                        ...styles.colObligation,
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <View
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        <Checkbox isChecked={obligation.isMet} />
                                        <Text style={styles.tableCell}>
                                            {t(obligation.label)}
                                        </Text>
                                    </View>
                                    {!obligation.isMet && obligation.gapReason ? (
                                        <Text style={styles.gapReason}>
                                            {t(obligation.gapReason)}
                                        </Text>
                                    ) : null}
                                </View>
                                <View
                                    style={{
                                        ...styles.tableCol,
                                        ...styles.colStatus,
                                        ...styles.borderRightNone,
                                    }}
                                >
                                    <Text style={styles.tableCell}>
                                        {obligation.statusLabel
                                            ? t(obligation.statusLabel)
                                            : "-"}
                                    </Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

const AnswersSection = ({ answerSections = [], t }) => {
    return (
        <View>
            <Text style={styles.sectionTitle}>
                {`3. ${t("Assessment Responses")}`}
            </Text>

            {answerSections.length === 0 ? (
                <Text style={styles.mutedText}>
                    {t("No assessment responses available.")}
                </Text>
            ) : (
                answerSections.map((section) => (
                    <View key={section.key}>
                        <Text style={{ ...styles.pText, ...styles.fontBold }}>
                            {t(section.title)}
                        </Text>

                        <View style={styles.table}>
                            <View style={styles.tableRow}>
                                <View
                                    style={{
                                        ...styles.tableColHeader,
                                        ...styles.colQuestion,
                                    }}
                                >
                                    <Text
                                        style={{
                                            ...styles.tableCell,
                                            ...styles.fontBold,
                                        }}
                                    >
                                        {t("Question")}
                                    </Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.tableColHeader,
                                        ...styles.colAnswer,
                                        ...styles.borderRightNone,
                                    }}
                                >
                                    <Text
                                        style={{
                                            ...styles.tableCell,
                                            ...styles.fontBold,
                                        }}
                                    >
                                        {t("Answer")}
                                    </Text>
                                </View>
                            </View>

                            {section.items.map((item, index) => {
                                const rowStyle = {
                                    ...styles.tableRow,
                                    ...((index + 1 >= section.items.length) &&
                                        styles.tableLastRow),
                                };

                                return (
                                    <View style={rowStyle} key={item.key || index}>
                                        <View
                                            style={{
                                                ...styles.tableCol,
                                                ...styles.colQuestion,
                                            }}
                                        >
                                            <Text style={styles.tableCell}>
                                                {t(item.label)}
                                            </Text>
                                        </View>
                                        <View
                                            style={{
                                                ...styles.tableCol,
                                                ...styles.colAnswer,
                                                ...styles.borderRightNone,
                                            }}
                                        >
                                            <Text style={styles.tableCell}>
                                                {t(String(item.value))}
                                            </Text>
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                ))
            )}
        </View>
    );
};

const EvidenceTable = ({ items = [], t }) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <View style={styles.table}>
            <View style={styles.tableRow}>
                <View
                    style={{
                        ...styles.tableColHeader,
                        ...styles.colTitle,
                    }}
                >
                    <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
                        {t("Title")}
                    </Text>
                </View>
                <View
                    style={{
                        ...styles.tableColHeader,
                        ...styles.colCategory,
                    }}
                >
                    <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
                        {t("Category")}
                    </Text>
                </View>
                <View
                    style={{
                        ...styles.tableColHeader,
                        ...styles.colType,
                        ...styles.borderRightNone,
                    }}
                >
                    <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
                        {t("Type")}
                    </Text>
                </View>
            </View>

            {items.map((item, index) => {
                const rowStyle = {
                    ...styles.tableRow,
                    ...((index + 1 >= items.length) && styles.tableLastRow),
                };

                return (
                    <View style={rowStyle} key={item.id || index}>
                        <View
                            style={{
                                ...styles.tableCol,
                                ...styles.colTitle,
                            }}
                        >
                            <Text style={styles.tableCell}>{item.title}</Text>
                        </View>
                        <View
                            style={{
                                ...styles.tableCol,
                                ...styles.colCategory,
                            }}
                        >
                            <Text style={styles.tableCell}>
                                {item.category ? t(item.category) : "-"}
                            </Text>
                        </View>
                        <View
                            style={{
                                ...styles.tableCol,
                                ...styles.colType,
                                ...styles.borderRightNone,
                            }}
                        >
                            <Text style={styles.tableCell}>
                                {item.type ? t(String(item.type)) : "-"}
                            </Text>
                        </View>
                    </View>
                );
            })}
        </View>
    );
};

const EvidenceSection = ({
    attachments = [],
    confluenceDocuments = [],
    t,
}) => {
    const hasAttachments = attachments.length > 0;
    const hasConfluence = confluenceDocuments.length > 0;

    return (
        <View>
            <Text style={styles.sectionTitle}>
                {`4. ${t("Evidence References")}`}
            </Text>

            {!hasAttachments && !hasConfluence ? (
                <Text style={styles.mutedText}>
                    {t("No evidence attachments available.")}
                </Text>
            ) : (
                <>
                    {hasAttachments && (
                        <>
                            <Text style={{ ...styles.pText, ...styles.fontBold }}>
                                {t("Attachments")}
                            </Text>
                            <EvidenceTable items={attachments} t={t} />
                        </>
                    )}

                    {hasConfluence && (
                        <>
                            <Text style={{ ...styles.pText, ...styles.fontBold }}>
                                {t("Confluence")}
                            </Text>
                            <EvidenceTable items={confluenceDocuments} t={t} />
                        </>
                    )}
                </>
            )}
        </View>
    );
};

const PageContent = ({ mapped = {}, t }) => {
    return (
        <View style={styles.section}>
            <ReadinessSection readiness={mapped.readiness || {}} t={t} />
            <ObligationsSection
                obligations={mapped.obligations || []}
                t={t}
            />
            <AnswersSection
                answerSections={mapped.answerSections || []}
                t={t}
            />
            <EvidenceSection
                attachments={mapped.attachments || []}
                confluenceDocuments={mapped.confluenceDocuments || []}
                t={t}
            />
        </View>
    );
};

export default withNamespaces()(PageContent);
